import { clinicId, createdById, userType } from "../../../constants";
import { SetStateFunctionType } from "../../types";
import dayjs from "dayjs";

export type ProcedureType = {
  procedureId: number;
  patientId: number;
  visitId: number;
  cptId: number;
  cptGroupId?: number; // Added
  encounterId: number;
  reason: string; // Added
  toothNo: string;
  quantity: number;
  amount: number;
  priorAuthorizationCode: string;
  status: number;
  procedureStatus?: string;
  ip: string;
  companyId: string;
  isApprovalRequired: boolean;
  isDenialCode: boolean | number | null; // Updated
  isPerformed: boolean;
  isDentalExam: number;
  isService: number;
  isExclude: boolean;
  createdId: number;
  clinicId: number;
  isBilled: number;
  insDiscount: number;
  insAmount: number;
  pShare: number;
  actualPatshare?: number | null;
  unitRate: number;
  createdDate: string | Date | null; // Updated
  isModified: number;
  userType: number;
  nursingProcedure: boolean;
  nursingRemarks: string | null; // Updated
  revertFlag: boolean;
  actionCode: string;
  insCoverage: number;
  hl7MsgStatus: number;
  cptDetails?: any | null; // Added
  paymentMode: number | null;
  doctorsRemarks: string | null;
  remarks: string | null;
  discountAmount: number | null;
  discountPercentage: number | null;
  discountType: string;
};


export type ProcedureFormValueType = {
  procedureId: number;
  cpt_code: CptCodeType | null;
  rate: string;
  amount: string;
  quantity: string;
  pShare: string;
  remarks: string;
  isPerformed: boolean;
  isExcluded: boolean;
  isApprovalRequired: boolean;
  nursingProcedure: boolean;
  nursingRemarks: string;
  encounterId: string;
  patientId: string;
  createdDate?: any;
  insuranceCoverage: Boolean | number
  discount: string | number;
  discountType: string;
  insDiscount?: number;
  procedureStatus: string;
};

export type CptCodeType = {
  cptId: number;
  insCoverage: number;
  cpt_status?: number;
  cptRate?: number;
  cptCode?: string;
  cptName?: string;
  cptDes?: string;
  ins_ref_id?: number;
  cptGroupId?: number;
  approvalRequired?: number;
  isExclude?: number;
  isFavorite?: number;
};


export type CptCodeSearchType = {
  search: string;
  poID: number;
  encounterId: number;
};

export type CptRateType = {
  needLonicCode: boolean;
  cpt_id: number;
  cpt_status: number;
  cpt_rate: number;
  isPackage: boolean;
  cpt_code: string;
  lonicCode: string;
  lonicValue: number;
  cpt_name: string;
  cpt_des: string;
  cptGroup_id: number;
  lonicUnit: string;
};

export type ProcedureContextType = {
  list: ProcedureTableType[];
  setList: SetStateFunctionType<ProcedureTableType[]>;
  edit: ProcedureFormValueType | null;
  setEdit: SetStateFunctionType<ProcedureFormValueType | null>;
  refetch: () => void
};

export type FetchPatientShareParamType = {
  cptId: number;
  amount: number;
  qty: number;
  encounter: number;
  cptGroupId: number;
  id: number;
  clinicId: number;
  editFlag: number;
};

export type PatientRateType = {
  rate: number;
  qty: number;
  amount: number;
  patShare: number;
  insDiscount: number;
  insAmount: number;
};

export type PrescriptionDeletePayload = {
  id: number;
  clinicId: string;
  isPerformed: boolean;
  item_id: number;
};

export const initialData: ProcedureType = {
  procedureId: 0,
  patientId: 0,
  visitId: 0,
  cptId: 0,
  encounterId: 0,
  toothNo: "",
  quantity: 0,
  amount: 0,
  priorAuthorizationCode: "",
  status: 1,
  ip: "",
  companyId: "",
  isApprovalRequired: false,
  isDenialCode: null,
  isPerformed: false,
  isDentalExam: 0,
  isService: 0,
  isExclude: false,
  createdId: 0,
  clinicId: 0,
  isBilled: 0,
  insDiscount: 0,
  insAmount: 0,
  pShare: 0,
  unitRate: 0,
  createdDate: "",
  isModified: 0,
  userType: 0,
  nursingProcedure: false,
  nursingRemarks: null,
  revertFlag: false,
  actionCode: "",
  insCoverage: 0,
  hl7MsgStatus: 0,
  reason: "",
  cptGroupId: 4,
  paymentMode: 0,
  doctorsRemarks: "",
  remarks: "",
  discountAmount: 0,
  discountPercentage: 0,
  discountType: "",
};


export type ProcedureTableType = {
  status: number;
  procedureStatus?: string;
  nursingProcedure: boolean;
  priorAuthorizationCode: string;
  isApprovalRequired: boolean;
  userType: number;
  isExcluded: boolean;
  procedureId: number;
  isDenialCode: number;
  isBilled: number;
  insCoverage: number;
  patientId: number;
  isModified: number;
  quantity: number;
  companyId: string;
  isDentalExam: number;
  nursingRemarks: string;
  createdById: number;
  createdByDate: string; // Consider using Date if you plan to parse it
  isPerformed: boolean;
  isService: number;
  cptGroupId: number;
  rate: number;
  visitId: number;
  amount: number;
  ip: string;
  cptId: number;
  pshare: number;
  remarks: string;
  itemId: number;
  cptName: string;
  cptCode: string;
  toothno: string;
  insAmount: number;
  cptRate?: number;
  doctorsRemarks?: string
  createdDate?: string;
  discountAmount?: number;
  discountPercentage?: number;
  discountType?: string;
};

export type GetProcedureParams = {
  queryParams: {
    filter: {
      status: number;
    };
    sortOrder: string;
    sortField: string;
    pageNumber: number;
    pageSize: number;
    clinicId: number;
  };
  encounterID: number;
};

export const defaultValues: Omit<
  ProcedureFormValueType,
  "procedureId" | "nursingRemarks" | "createdById"
> = {
  amount: "",
  cpt_code: null,
  isApprovalRequired: false,
  isPerformed: false,
  nursingProcedure: false,
  isExcluded: false,
  pShare: "",
  quantity: "",
  rate: "",
  remarks: "",
  encounterId: "",
  patientId: "",
  insuranceCoverage: false,
  discount: 0,
  discountType: "Percentage",
  procedureStatus: "PLANNED"
};

export type ProcedureRateType = {
  insRate: number | null;
  isExcluded: boolean | null;
  approvalRequired: boolean | null;
  cptId?: number | null;
  cptGroupID?: string | null;
  cptName?: string | null;
};

export const prepareProcedurePayload = (
  data: ProcedureFormValueType & { paymentMode: number, fromVerification?: boolean },
  insDiscount = 0,
): ProcedureType => {
  let amount = Number(data.rate || "0")
  amount = amount * (Number(data.quantity));
  console.log("[prepareProcedurePayload] =>", data?.createdDate)

  const computeActualPatShare = () => {
    const after = Number(data?.pShare || 0);
    const discount = Number(data?.discount || 0);
    const discountType = String(data?.discountType || "Percentage").toLowerCase();
    if (discountType === "percentage") {
      const pct = Math.min(Math.max(discount, 0), 100);
      const denom = 1 - pct / 100;
      if (denom <= 0) return 0;
      const base = after / denom;
      return Number.isFinite(base) ? Number(base.toFixed(2)) : 0;
    }
    const base = after + Math.max(discount, 0);
    return Number.isFinite(base) ? Number(base.toFixed(2)) : 0;
  };

  return {
    procedureId: data.procedureId || 0,
    patientId: Number(data.patientId),
    visitId: 0,
    cptId: data.cpt_code?.cptId as number,
    insCoverage: Number(data.insuranceCoverage),
    pShare: Number(data.pShare),
    actualPatshare: computeActualPatShare(),
    insAmount: (data.paymentMode == 1 && data.insuranceCoverage != 0) ? Number(data.amount) : 0,
    createdId: createdById as number,
    createdDate: data?.createdDate ? dayjs(data?.createdDate).format('YYYY-MM-DDTHH:mm:ss') : null,
    insDiscount: Number(insDiscount || 0),
    userType: userType as number,
    revertFlag: false,
    actionCode: "",
    hl7MsgStatus: 0,
    encounterId: Number(data.encounterId),
    toothNo: "",
    quantity: Number(data.quantity),
    amount: Number(amount.toFixed(2)), // Ensure amount is a number and formatted to two decimal places
    // amount: data.paymentMode == 1 ? parseFloat(data.rate) * parseInt(data.quantity) :  parseFloat(data.amount),
    priorAuthorizationCode: "",
    status: 1,
    procedureStatus: data.procedureStatus || "PLANNED",
    ip: "",
    companyId: "",
    isApprovalRequired: data?.isApprovalRequired || false,
    isDenialCode: 0,
    isDentalExam: 0,
    isPerformed: data.isPerformed,
    isService: 0,
    isExclude: data?.isExcluded || false,
    isModified: 0,
    isBilled: 0,
    unitRate: Number(data.rate),
    nursingProcedure: data.nursingProcedure,
    nursingRemarks: data.nursingRemarks || "",
    clinicId,
    reason: "",
    cptGroupId: 4,
    cptDetails: null,
    paymentMode: data.paymentMode,
    doctorsRemarks: String(data.remarks || ""),
    remarks: String(data.remarks || ""),
    discountAmount: data.discountType == "Percentage" ? null : Number(data.discount),
    discountPercentage: data.discountType == "Percentage" ? Number(data.discount) : null,
    discountType: data.discountType,
  };
};
